// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { AppShell } from "@/components/shell/AppShell";
import { platformAdminNav, platformAdminFooterNav } from "@/config/platformAdminNav";
import AreaUserMenu from "@/components/common/AreaUserMenu";
import { useModules } from "@/hooks/useModules";
import { filterNavByModules } from "@/lib/navModules";
import { useEventNav } from "@/hooks/useEventNav";
import EventNavHeader from "@/components/shell/EventNavHeader";

import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const AdminDashboardLayout: React.FC = () => {
  const { user, profile, isLoading } = useAuth();
  const navigate = useNavigate();
  const { isEnabled } = useModules();
  const eventNav = useEventNav(isEnabled);

  const [isSchedulingUser, setIsSchedulingUser] = useState(false);
  const [roleCheckComplete, setRoleCheckComplete] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    if (!user) {
      navigate('/auth/login', { state: { from: '/admin-dashboard' } });
      return;
    }

    // Quick sync check for admin/org_admin profile roles
    if (profile?.role === 'admin' || profile?.role === 'org_admin') {
      setHasAccess(true);
      setIsSchedulingUser(false);
      setRoleCheckComplete(true);
      return;
    }

    // Async check for org_admin or scheduling in user_roles table
    const checkSpecialRoles = async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .in('role', ['org_admin', 'scheduling', 'admin', 'rlevents_super_admin']);

      const isOrgAdmin = data?.some(r => r.role === 'org_admin') || false;
      const isScheduling = data?.some(r => r.role === 'scheduling') || false;
      const isAdmin = data?.some(r => r.role === 'admin' || r.role === 'rlevents_super_admin') || false;

      if (isAdmin || isOrgAdmin || isScheduling) {
        setHasAccess(true);
        setIsSchedulingUser(isScheduling && !isAdmin);
      } else {
        toast.error("You don't have admin privileges to access this area");
        navigate('/dashboard');
      }
      setRoleCheckComplete(true);
    };

    checkSpecialRoles();
  }, [user, profile, isLoading, navigate]);

  // Show loading while auth or role check is pending
  if (isLoading || !roleCheckComplete) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-lg font-medium">Loading...</span>
      </div>
    );
  }

  if (!user || !hasAccess) {
    return null;
  }

  const userRole = profile?.role === "org_admin" ? "Org Admin" : "Admin";

  const brand = (
    <Link to="/admin-dashboard" className="flex items-center gap-2 overflow-hidden">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-sm font-bold text-sidebar-primary-foreground">
        RL
      </span>
      <span className="truncate text-lg font-bold tracking-tight text-sidebar-foreground group-data-[collapsible=icon]:hidden">
        RLevents
      </span>
    </Link>
  );

  return (
    <AppShell
      navGroups={eventNav ? eventNav.groups : filterNavByModules(platformAdminNav, isEnabled)}
      footerNav={eventNav ? undefined : platformAdminFooterNav}
      navHeader={
        eventNav ? (
          <EventNavHeader
            name={eventNav.name}
            startDate={eventNav.startDate}
            endDate={eventNav.endDate}
            status={eventNav.status}
            schedulePublished={eventNav.schedulePublished}
          />
        ) : undefined
      }
      brand={brand}
      userMenu={
        <AreaUserMenu roleLabel={userRole} settingsPath="/admin-dashboard/settings" />
      }
    >

      <div data-page-pad className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <Outlet />
      </div>
    </AppShell>
  );
};

export default AdminDashboardLayout;
